"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { getCollectionImageUrl } from "@/lib/cloudinary";

// Images
const CasaDelGrey = getCollectionImageUrl("2022-casa-del-grey", "ALEXANDRA-GREY-CASA-DEL-GREY-06");
const LightGrey = getCollectionImageUrl("2021-light-grey", "ALEXANDRA-GREY-LIGHT-GREY-11");
const NaturalGrey = getCollectionImageUrl("2023-natural-grey", "ALEXANDRA-GREY-NATURAL-GREY-01");

const designs = [
  {
    src: CasaDelGrey,
    alt: "Casa Del Grey",
    path: CasaDelGrey,
  },
  {
    src: LightGrey,
    alt: "Light Grey",
    path: LightGrey,
  },
  {
    src: NaturalGrey,
    alt: "Natural Grey",
    path: NaturalGrey,
  },
];

const salons = [
  {
    name: "AU GREY DES COULEURS",
    address: {
      street: "1 Rue Eugène Varlin",
      postalCode: "94800",
      city: "Villejuif",
    },
    planityLink: "https://www.planity.com/au-grey-des-couleurs-94800-villejuif",
  },
  {
    name: "ALEXANDRA GREY VILLEJUIF",
    address: {
      street: "61 Rue Jean Jaurès",
      postalCode: "94800",
      city: "Villejuif",
    },
    planityLink: "https://www.planity.com/alexandra-grey-villejuif-94800",
  },
  {
    name: "ALEXANDRA GREY VILLE-D'AVRAY",
    address: {
      street: "28 Rue de Saint Cloud",
      postalCode: "92410",
      city: "Ville-d'Avray",
    },
    planityLink: "https://www.planity.com/alexandra-grey-ville-davray-92410",
  },
];

type Props = {
  initialDesign: string;
};

export const GiftCard = ({ initialDesign }: Props) => {
  const [selectedImage, setSelectedImage] = useState<string>(() => {
    const design = designs.find((d) => d.path === initialDesign);
    return design?.src || CasaDelGrey;
  });

  return (
    <div className="flex w-full flex-col gap-16 p-4 px-4 py-20 ph:px-24 md:px-48 lg:px-72 xl:px-96">
      {/* Preview and Form Section */}
      <div className="flex flex-col gap-8 md:flex-row md:gap-16">
        {/* Left: Preview */}
        <div className="relative h-64 w-full sm:h-[350px] sm:w-[500px] md:h-[300px] md:w-[550px] lg:h-[400px] lg:w-[650px]">
          <div className="relative h-full w-full">
            <Image
              src={selectedImage}
              alt="Carte cadeau preview"
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
              className="rounded-xl object-cover"
            />
          </div>
        </div>

        {/* Right: Content */}
        <div className="flex w-full flex-col gap-8 md:w-2/5">
          <div className="space-y-4">
            <h2 className="text-4xl uppercase ph:text-5xl">
              OFFREZ UN MOMENT DE BONHEUR
            </h2>
            <p className="text-gray-600">
              Pour son anniversaire, pour sa fête ou juste pour faire plaisir,
              nos cartes cadeaux personnalisables sont entièrement digitalisées.
              Choisissez son design, sa valeur et envoyez la directement !
            </p>
          </div>

          {/* Design Selection */}
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">DESIGN</h3>
            <div className="flex gap-4">
              {designs.map((design) => (
                <button
                  key={design.alt}
                  onClick={() => setSelectedImage(design.src)}
                  className={`group relative aspect-[850/550] w-32 overflow-hidden rounded-lg transition-all hover:opacity-80 ${selectedImage === design.src
                      ? "ring-2 ring-gray-800"
                      : "ring-1 ring-gray-200"
                    }`}
                >
                  <Image
                    src={design.src}
                    alt={design.alt}
                    fill
                    sizes="(max-width: 768px) 100vw, 50vw"
                    className={`object-cover transition-all ${selectedImage === design.src
                        ? ""
                        : "grayscale hover:grayscale-0"
                      }`}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Salons Grid */}
      <div className="grid grid-cols-1 gap-16 md:grid-cols-3">
        {salons.map((salon) => (
          <div key={salon.name} className="flex flex-col">
            <h2 className="mb-6 text-2xl">{salon.name}</h2>
            <div className="text-semibold mb-6 space-y-1 font-bold">
              <p>{salon.address.street}</p>
              <p>
                {salon.address.postalCode} {salon.address.city}
              </p>
            </div>
            <div className="mt-auto space-y-4">
              <p className="text-sm text-gray-600">
                Une fois sur la page du salon, cliquez sur le bouton
                &ldquo;Offrir&rdquo; en haut à droite.
              </p>
              <Link
                href={salon.planityLink}
                className="group relative inline-block pb-[2px]"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>ACCÉDER AU SALON</span>
                <div className="absolute bottom-0 left-0 h-[1px] w-full bg-black">
                  <div className="h-full w-0 bg-gold transition-all duration-300 ease-out group-hover:w-full"></div>
                </div>
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GiftCard;
